import { useCallback, useEffect, useRef } from "react";

/**
 * A16 — Persistent filters per route+user.
 *
 * Reads a snapshot of the caller's filter state from localStorage on mount
 * (`applySnapshot(snapshot)`) and writes it back on every change. Storage key:
 *   `mh_filters_v1_${route}_${userId}`
 *
 * Pagination page number is intentionally NOT included by the caller —
 * every mount starts on page 1.
 *
 * Usage:
 *   usePersistedFilters({
 *     route: "orders",
 *     userId: user?.id,
 *     snapshot: { statuses, fromDate, untilDate, search },
 *     apply: ({ statuses, fromDate, untilDate, search }) => { ... },
 *   });
 */
export function usePersistedFilters({ route, userId, snapshot, apply }) {
  const applyRef = useRef(apply);
  applyRef.current = apply;
  const restored = useRef(false);

  const key = userId ? `mh_filters_v1_${route}_${userId}` : null;

  // Restore once per (route, user) — after that, snapshot writes dominate.
  useEffect(() => {
    if (!key || restored.current) return;
    restored.current = true;
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        applyRef.current?.(parsed);
      }
    } catch {
      /* corrupt entry — ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Persist snapshot on every change. Skip until restore has run so we
  // don't overwrite persisted state with the caller's initial defaults.
  useEffect(() => {
    if (!key || !restored.current) return;
    try {
      window.localStorage.setItem(key, JSON.stringify(snapshot));
    } catch {
      /* quota / private-browsing — ignore */
    }
  }, [key, snapshot]);

  const clear = useCallback(() => {
    if (!key) return;
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }, [key]);

  return { clear };
}
